import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  IconButton,
  Grid,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Switch,
  InputAdornment,
  TextField,
  Paper,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Badge,
  CircularProgress,
} from '@mui/material'
import {
  Bolt as BoltIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Favorite as FavoriteIcon,
  Work as WorkIcon,
  Search as SearchIcon,
  InfoOutlined as InfoIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  EventBusy as EventBusyIcon,
  AccountCircle as AccountCircleIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
  Close as CloseIcon2,
} from '@mui/icons-material'
import { AppSidebar } from '@/components/AppSidebar'
import {
  Profile,
  ROOM_TYPE_LABELS,
  RoomType,
} from '@/types/profile'
import { ProfileListCard } from '@/components/ProfileListCard'
import { getRoom, getRoomProfiles, RoomDetailResponse, ProfileResponse } from '@/services/roomService'
import { toRoomType } from '@/utils/roomType'
import { resolveOwnerName, ROOM_OWNER_DEFAULT_NAME } from '@/utils/roomOwner'
import { useAuth } from '@/contexts/AuthContext'
import { useMyProfile } from '@/contexts/ProfileContext'
import { useNotifications } from '@/contexts/NotificationContext'

export const RoomPage = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { user } = useAuth()
  const { getMyProfile } = useMyProfile()

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [myProfileOpen, setMyProfileOpen] = useState(false)
  const [showChattingOnly, setShowChattingOnly] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { unreadByUser, totalUnread, unreadDetails } = useNotifications()
  const [notificationDismissed, setNotificationDismissed] = useState(false)

  // 新着メッセージが増えたら通知バーを再表示
  useEffect(() => {
    setNotificationDismissed(false)
  }, [totalUnread])
  const [infoOpen, setInfoOpen] = useState(false)
  const [room, setRoom] = useState<RoomDetailResponse | null>(null)
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!roomId) return
      
      try {
        setLoading(true)
        setError(null)
        
        const roomData = await getRoom(roomId)
        setRoom(roomData)

        const profilesData = await getRoomProfiles(roomId)
        const roomProfileType = toRoomType(roomData.categoryType)
        const mappedProfiles = profilesData.map((p) => mapProfileResponseToProfile(p, roomProfileType))
        setProfiles(mappedProfiles)
      } catch (err) {
        console.error('Failed to fetch room data:', err)
        const code = (err as { code?: string }).code
        if (code === 'functions/permission-denied') {
          setError('このルームに参加していません。QRコードから入室手続きを行ってください。')
        } else if (code === 'functions/not-found') {
          setError('ルームが見つかりません')
        } else {
          setError('ルーム情報の取得に失敗しました')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [roomId])

  /**
   * 参加者のプロフィールを画面表示用に変換する。
   *
   * 表示する種別はルームのカテゴリで決める。参加者個人の eventType で
   * 切り替えると、婚活ルームなのにビジネスのプロフィールが表示される。
   */
  const mapProfileResponseToProfile = (response: ProfileResponse, profileType: RoomType): Profile => {
    const base = {
      id: response.userId,
      type: profileType,
      pr: response.prText,
      photoUrl: undefined,
      createdAt: new Date(),
      isRoomOwner: Boolean(response.isRoomOwner),
    }
    // 主催者はプロフィール未登録でも一覧に出るため、氏名が空なら補う。
    // Googleの表示名があればそれを、無ければ抽象名「管理者」を使う。
    const fallbackName = resolveOwnerName(null, response.username)

    if (profileType === 'business') {
      return {
        ...base,
        type: 'business' as const,
        name: response.bizName || (response.isRoomOwner ? fallbackName : ''),
        companyName: response.bizCompanyName || '',
        position: response.bizPosition || '',
        businessContent: response.bizContent || '',
        comment: response.bizComment || '',
        wantToMatch: response.bizTargetIndustry || '',
        notWantToMatch: response.bizExcludeIndustry || '',
        contactEmail: response.isEmailPublic ? response.bizEmail || response.mailAddress || '' : '',
        contactPhone: response.isTelPublic ? response.bizTel || '' : '',
      }
    } else if (profileType === 'dating') {
      return {
        ...base,
        type: 'dating' as const,
        name: response.loveNickname || (response.isRoomOwner ? fallbackName : ''),
        age: response.loveAge || 0,
        occupation: response.loveJob || '',
        gender: (response.loveGender || '') as 'male' | 'female' | 'other',
        annualIncome: response.loveAnnualIncome || '',
        wantChildren: response.loveWantsKids || undefined,
        userNumber: response.userId,
        wantToMatch: response.loveTargetPerson || '',
        notWantToMatch: response.loveExcludePerson || '',
        contactEmail: response.isEmailPublic ? response.loveContactInfo || response.mailAddress || '' : '',
        contactPhone: response.isTelPublic ? response.loveTel || '' : '',
      }
    } else {
      return {
        ...base,
        type: 'job' as const,
        name: response.careerName || (response.isRoomOwner ? fallbackName : ''),
        companyName: response.careerPrevCompany || '',
        lastPosition: response.careerLastPosition || '',
        education: response.careerEducationLevel || '',
        schoolName: response.careerSchoolName || '',
        age: response.careerAge || 0,
        gender: (response.careerGender || '') as 'male' | 'female' | 'other',
        comment: response.careerComment || '',
        wantToMatch: response.careerTargetCompany || '',
        notWantToMatch: response.careerExcludeConditions || '',
        contactEmail: response.isEmailPublic ? response.careerEmail || response.mailAddress || '' : '',
        contactPhone: response.isTelPublic ? response.careerTel || '' : '',
      }
    }
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !room) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
        <Alert severity="error">{error || 'ルームが見つかりません'}</Alert>
      </Box>
    )
  }

  const isExpired = room.expirationDate != null && new Date(room.expirationDate) < new Date()

  if (isExpired) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, px: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 480, width: '100%', borderRadius: 2 }}>
          <Typography fontWeight={700} gutterBottom>このルームは有効期限切れです</Typography>
          <Typography variant="body2">
            有効期限: {new Date(room.expirationDate!).toLocaleString('ja-JP')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            このルームには入室できません。
          </Typography>
        </Alert>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
          ホームに戻る
        </Button>
      </Box>
    )
  }

  const handleChatClick = (profile: Profile) => {
    navigate(`/chat/${profile.id}?roomId=${room.roomId}`)
  }

  const handleProfileClick = (profile: Profile) => {
    navigate(`/profile/${profile.id}?roomId=${room.roomId}`)
  }

  const roomType = toRoomType(room.categoryType)

  const getRoomIcon = () => {
    switch (roomType) {
      case 'business':
        return <BusinessIcon />
      case 'dating':
        return <FavoriteIcon />
      case 'job':
        return <WorkIcon />
      default:
        return <BusinessIcon />
    }
  }

  const getRoomColor = () => {
    switch (roomType) {
      case 'business':
        return 'primary.main'
      case 'dating':
        return '#ec4899'
      case 'job':
        return '#059669'
      default:
        return 'primary.main'
    }
  }

  // ── モーダル内の行コンポーネント ──────────────────────────
  const InfoRow = ({
    icon,
    label,
    value,
    placeholder = '未設定',
    multiline = false,
    masked = false,
  }: {
    icon?: React.ReactNode
    label: string
    value?: string
    placeholder?: string
    multiline?: boolean
    masked?: boolean
  }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        {icon && <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>}
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'action.hover',
          borderRadius: 1.5,
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
          color: value ? 'text.primary' : 'text.disabled',
          fontFamily: masked ? 'monospace' : undefined,
          letterSpacing: masked ? '0.15em' : undefined,
        }}
      >
        {masked && value ? '●'.repeat(Math.min(value.length, 8)) : (value || placeholder)}
      </Typography>
    </Box>
  )

  // フリーワード検索: プロフィールの文字列プロパティを結合してマッチ判定
  const getSearchableText = (profile: Profile): string => {
    const common = [profile.name, profile.pr, profile.wantToMatch, profile.notWantToMatch]
    const typeFields =
      profile.type === 'business'
        ? [profile.companyName, profile.position, profile.businessContent, profile.comment]
        : profile.type === 'dating'
        ? [profile.occupation, String(profile.age)]
        : [profile.companyName, profile.lastPosition, profile.education, profile.schoolName, profile.comment]
    return [...common, ...typeFields].filter(Boolean).join(' ').toLowerCase()
  }

  const matchesSearch = (profile: Profile): boolean => {
    const q = searchQuery.trim()
    if (!q) return true
    const text = getSearchableText(profile)
    return q.split(/\s+/).every((word) => text.includes(word.toLowerCase()))
  }

  const renderProfileCard = (profile: Profile) => (
    <ProfileListCard
      profile={profile}
      isMobile={isMobile}
      unreadCount={unreadByUser[profile.id] ?? 0}
      onChatClick={handleChatClick}
      onProfileClick={handleProfileClick}
    />
  )

  // フィルタリングとソート
  // 主催者は問い合わせ先として一覧に出すが「参加者数」には含めない。
  // ホーム画面のカード(t_room_usersの件数)と数が食い違うため。
  const participantCount = profiles.filter((p) => !p.isRoomOwner).length

  const sortedParticipants = [...profiles]
    .filter((profile) => profile.id !== user?.userId?.toString())
    .filter((profile) => matchesSearch(profile))
    // 「チャット中のみ表示」: やり取りのある相手(未読がある相手)に絞る。
    // 主催者は問い合わせ先として常に残す。
    .filter((profile) =>
      !showChattingOnly || profile.isRoomOwner || (unreadByUser[profile.id] ?? 0) > 0,
    )
    // 主催者は問い合わせ先として目立つよう先頭に固定する
    .sort((a, b) => Number(Boolean(b.isRoomOwner)) - Number(Boolean(a.isRoomOwner)))


  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ px: { xs: 1.5, sm: 2 } }}>
          <IconButton
            edge="start"
            onClick={() => navigate('/')}
            sx={{ mr: 1, display: { xs: 'none', sm: 'flex' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Box
              sx={{
                width: { xs: 32, sm: 36 },
                height: { xs: 32, sm: 36 },
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BoltIcon sx={{ color: 'white', fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Box>
              <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                {room.roomName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ color: getRoomColor(), display: 'flex', fontSize: '0.8rem' }}>
                  {getRoomIcon()}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {ROOM_TYPE_LABELS[roomType]} • {participantCount}人
                </Typography>
              </Box>
            </Box>
          </Box>
          <IconButton onClick={() => setMyProfileOpen(true)} size="small" sx={{ ml: 0.5 }}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton onClick={() => setInfoOpen(true)} size="small" sx={{ ml: 0.5 }}>
            <InfoIcon />
          </IconButton>
          {isMobile && (
            <IconButton onClick={() => setSidebarOpen(true)}>
              <Badge badgeContent={totalUnread} color="error" max={99}>
                <MenuIcon />
              </Badge>
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <AppSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        showBackToHome
        roomId={room.roomId}
      />

      {/* ルーム情報モーダル */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            <Typography fontWeight={700}>ルーム情報</Typography>
          </Box>
          <IconButton size="small" onClick={() => setInfoOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Divider sx={{ mb: 2 }} />

          {/* ルーム名 */}
          <InfoRow label="ルーム名" value={room.roomName} />

          {/* カテゴリタイプ */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              カテゴリタイプ
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                icon={<Box sx={{ color: getRoomColor(), display: 'flex' }}>{getRoomIcon()}</Box>}
                label={ROOM_TYPE_LABELS[roomType]}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* 開催日時 */}
          <InfoRow
            icon={<CalendarIcon fontSize="small" />}
            label="開催日時"
            value={room.eventDatetime ? new Date(room.eventDatetime).toLocaleString('ja-JP') : '未設定'}
          />

          {/* 有効期限 */}
          <InfoRow
            icon={<EventBusyIcon fontSize="small" />}
            label="有効期限"
            value={room.expirationDate ? new Date(room.expirationDate).toLocaleString('ja-JP') : '未設定'}
          />

          <Divider sx={{ my: 2 }} />

          {/* 最大参加者数 */}
          <InfoRow
            icon={<PeopleIcon fontSize="small" />}
            label="最大参加者数"
            value={room.maxParticipants ? `${room.maxParticipants}人` : '未設定'}
          />

          <Divider sx={{ my: 2 }} />

          {/* 作成日時 */}
          <InfoRow
            icon={<CalendarIcon fontSize="small" />}
            label="作成日時"
            value={room.createdAt ? new Date(room.createdAt).toLocaleString('ja-JP') : '未設定'}
          />

          {/* 現在の参加者数 */}
          <InfoRow
            icon={<PeopleIcon fontSize="small" />}
            label="現在の参加者数"
            value={`${participantCount}人`}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="contained" fullWidth onClick={() => setInfoOpen(false)} sx={{ borderRadius: 2, fontWeight: 700 }}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* マイプロフィールモーダル */}
      {(() => {
        // ルーム内の自分のプロフィール(ルームのカテゴリで組み立て済み)を優先する。
        // ProfileContext は roomId を持たないため、本人の event_type 基準の
        // 1種類しか保持しておらず、婚活ルームでもビジネスが出てしまう。
        const myProfile = profiles.find((p) => p.id === user?.userId) ?? getMyProfile(roomType)

        return (
          <Dialog
            open={myProfileOpen}
            onClose={() => setMyProfileOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountCircleIcon color="primary" />
                <Typography fontWeight={700}>マイプロフィール</Typography>
              </Box>
              <IconButton size="small" onClick={() => setMyProfileOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 0 }}>
              <Divider sx={{ mb: 2 }} />

              {myProfile ? (
                <>
                  {/* プロフィールタイプ */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      利用中のプロフィール
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        icon={<Box sx={{ color: getRoomColor(), display: 'flex' }}>{getRoomIcon()}</Box>}
                        label={ROOM_TYPE_LABELS[roomType]}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* 共通: 名前 */}
                  <InfoRow label="名前" value={myProfile.name} />

                  {/* タイプ別フィールド */}
                  {myProfile.type === 'business' && (
                    <>
                      <InfoRow label="会社名" value={myProfile.companyName} />
                      <InfoRow label="役職" value={myProfile.position} />
                      <InfoRow label="事業内容" value={myProfile.businessContent} multiline />
                      <InfoRow label="コメント" value={myProfile.comment} multiline />
                    </>
                  )}

                  {myProfile.type === 'dating' && (
                    <>
                      <InfoRow label="年齢" value={myProfile.age ? `${myProfile.age}歳` : undefined} />
                      <InfoRow label="性別" value={myProfile.gender === 'male' ? '男性' : myProfile.gender === 'female' ? '女性' : 'その他'} />
                      <InfoRow label="職業" value={myProfile.occupation} />
                      <InfoRow label="年収" value={myProfile.annualIncome} />
                    </>
                  )}

                  {myProfile.type === 'job' && (
                    <>
                      <InfoRow label="前職" value={myProfile.companyName} />
                      <InfoRow label="直近の役職" value={myProfile.lastPosition} />
                      <InfoRow label="最終学歴" value={myProfile.education} />
                      <InfoRow label="学校名" value={myProfile.schoolName} />
                      <InfoRow label="年齢" value={myProfile.age ? `${myProfile.age}歳` : undefined} />
                      <InfoRow label="性別" value={myProfile.gender === 'male' ? '男性' : myProfile.gender === 'female' ? '女性' : 'その他'} />
                      <InfoRow label="コメント" value={myProfile.comment} multiline />
                    </>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* 共通: PR */}
                  <InfoRow label="自己PR" value={myProfile.pr} multiline />

                  {/* 共通: マッチしたい相手 */}
                  <InfoRow label="マッチしたい相手" value={myProfile.wantToMatch} multiline />
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <AccountCircleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary" gutterBottom>
                    このルームタイプのプロフィールが未登録です
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {ROOM_TYPE_LABELS[roomType]}用のプロフィールを登録してください
                  </Typography>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
              {myProfile && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setMyProfileOpen(false)
                    navigate(`/profile/${user?.userId}?roomId=${room.roomId}`)
                  }}
                  sx={{ borderRadius: 2, fontWeight: 700, flex: 1 }}
                >
                  詳細を見る
                </Button>
              )}
              <Button
                variant="contained"
                fullWidth={!myProfile}
                onClick={() => setMyProfileOpen(false)}
                sx={{ borderRadius: 2, fontWeight: 700, flex: myProfile ? 1 : undefined }}
              >
                閉じる
              </Button>
            </DialogActions>
          </Dialog>
        )
      })()}

      {/* 新着メッセージ通知バー */}
      {totalUnread > 0 && !notificationDismissed && (() => {
        const entries = Object.entries(unreadDetails)
        const displayItems = entries.slice(0, 3).map(([uid, detail]) => {
          const p = profiles.find(prof => prof.id === uid)
          const name = p?.name || (p?.isRoomOwner ? ROOM_OWNER_DEFAULT_NAME : '不明')
          return `${name}: ${detail.content}`
        })
        const extraCount = entries.length - 3
        const summaryText = extraCount > 0
          ? `${displayItems.join(' / ')} / 他${extraCount}件`
          : displayItems.join(' / ')
        return (
          <Alert
            icon={<ChatIcon />}
            severity="info"
            sx={{
              borderRadius: 0,
              py: 0.5,
              px: { xs: 2, sm: 3 },
              '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' },
            }}
            action={
              <IconButton size="small" color="inherit" onClick={() => setNotificationDismissed(true)}>
                <CloseIcon2 fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body2" fontWeight={600} noWrap>{summaryText}</Typography>
          </Alert>
        )
      })()}

      {/* 検索バー（AppBar直下に固定） */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: 1000,
          borderBottom: 1,
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          bgcolor: 'background.paper',
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="名前・会社・PRなどで検索…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              bgcolor: 'action.hover',
              '& fieldset': { border: 'none' },
              '&.Mui-focused': {
                bgcolor: 'background.paper',
                boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.light}`,
              },
            },
          }}
        />
      </Paper>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        {room.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, textAlign: 'center' }}
          >
            {room.description}
          </Typography>
        )}

        {/* フィルター + 件数 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {searchQuery.trim() ? (
            <Typography variant="body2" color="text.secondary">
              <strong>{sortedParticipants.length}</strong> 件ヒット
            </Typography>
          ) : (
            <Box />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={showChattingOnly}
                onChange={(e) => setShowChattingOnly(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                チャット中のみ表示
              </Typography>
            }
          />
        </Box>

        {/* プロフィールカード一覧 */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {sortedParticipants.map((profile) => (
            <Grid item xs={12} sm={6} md={4} key={profile.id}>
              {renderProfileCard(profile)}
            </Grid>
          ))}
        </Grid>

        {sortedParticipants.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {searchQuery.trim()
                ? `「${searchQuery}」に一致する参加者が見つかりません`
                : showChattingOnly
                  ? 'チャット中の相手がいません'
                  : 'まだ他の参加者がいません'}
            </Typography>
          </Box>
        )}
      </Container>

    </Box>
  )
}
